"use client";

import React, { useRef } from 'react';
import Image from 'next/image';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { imageUrl } from '@/lib/medusa';
import { isNoShop, getProductPriceInfo } from '@/lib/data/products';
import PriceTag from '@/components/PriceTag';
import { useTranslation } from '@/lib/i18n/useTranslation';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const DEFAULT_IMAGE = '/images/default-product-image.jpg';

const ProductGrid = ({ products, handleProductClick, selectedCategory, promotions = [] }) => {
    const { t, locale } = useTranslation();
    const containerRef = useRef(null);

    useGSAP(
        () => {
            const items = gsap.utils.toArray('.product-grid-item');
            if (items.length === 0) return;

            const mm = gsap.matchMedia();

            mm.add(
                {
                    motionOK: '(prefers-reduced-motion: no-preference)',
                    motionReduced: '(prefers-reduced-motion: reduce)',
                },
                (context) => {
                    const { motionOK } = context.conditions;

                    if (!motionOK) {
                        gsap.set(items, { autoAlpha: 1, y: 0 });
                        return;
                    }

                    gsap.set(items, { autoAlpha: 0, y: 24 });

                    ScrollTrigger.batch(items, {
                        start: 'top 90%',
                        once: true,
                        onEnter: (batch) =>
                            gsap.to(batch, {
                                autoAlpha: 1,
                                y: 0,
                                duration: 0.7,
                                ease: 'power2.out',
                                stagger: 0.08,
                                overwrite: true,
                            }),
                    });

                    ScrollTrigger.refresh();
                }
            );
        },
        { scope: containerRef, dependencies: [products], revertOnUpdate: true }
    );

    return (
        <div
            ref={containerRef}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 p-8"
        >
            {products.map((product) => {
                const thumb = product.thumbnail || product.images?.[0]?.url;
                const src = thumb ? imageUrl(thumb) : DEFAULT_IMAGE;
                const showPrice = selectedCategory === 'shop' && !isNoShop(product);
                const priceInfo = showPrice ? getProductPriceInfo(product, promotions) : null;
                const stock = product.variants?.[0]?.inventory_quantity ?? null;
                const isSold = stock === 0;
                return (
                    <div
                        key={product.id}
                        className="product-grid-item cursor-pointer"
                        onClick={() => handleProductClick(product)}
                    >
                        <div className="relative aspect-square">
                            <Image
                                src={src}
                                alt={product.title}
                                fill
                                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                className="object-cover"
                            />
                            {isSold && (
                                <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 text-xs">
                                    {t('home.sold', 'Sold')}
                                </div>
                            )}
                        </div>
                        <div className="mt-2">
                            <h2 className="text-lg font-thin">{product.title}</h2>
                            <div className="flex justify-between items-center">
                                <p className="text-sm font-thin flex-grow pr-2">
                                    {product.subtitle || product.description?.slice(0, 80)}
                                </p>
                                {priceInfo?.amount != null && (
                                    <p className="text-sm font-thin whitespace-nowrap">
                                        <PriceTag info={priceInfo} locale={locale} showBadge />
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ProductGrid;
